import React, { useState, useRef, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Avatar, 
  Paper, 
  Stack,
  IconButton,
  Tooltip,
  ButtonGroup,
  Chip
} from "@mui/material";
import PropTypes from 'prop-types';
import PersonIcon from "@mui/icons-material/Person";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import AddBoxIcon from '@mui/icons-material/AddBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

import { buildNestedTree } from "../../Domain/Services/OrgChartDataUtils";

import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { getAuthenticatedAvatarUrl } from 'Shared/Utils/Domain/Services/AvatarUrlService';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const MIN_SCALE = 0.2;
const MAX_SCALE = 3;
const SCALE_STEP = 0.12;
const WHEEL_ZOOM_SENSITIVITY = 0.0015;

const OrgNode = React.memo(({ node, collapsedIds, toggleNode, onNodeClick, highlightNodeId, hiddenIds, toggleVisibility, onExpandNode, loadingNodeIds, displayMode }) => {
  const isHidden = hiddenIds.has(node.id);
  const isSpecialMode = displayMode === 'holidays';
  if (isHidden) {
    return (
      <Tooltip title={`Mostrar "${node.text}"`}>
        <IconButton 
          size="small" 
          onClick={(e) => { e.stopPropagation(); toggleVisibility(node.id) }}
          sx={{ 
            bgcolor: 'grey.100', 
            border: '1px solid', 
            borderColor: 'divider',
            '&:hover': { bgcolor: 'primary.light', color: 'white' }
          }}
        >
          <AddCircleOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  const isPositionLinked = !!node.positionId;
  const isOfficialPosition = Boolean(node.isOfficialPosition || (node.positionId && node.jobPositionId));
  const isCustomPosition = Boolean(isPositionLinked && !isOfficialPosition);
  const mainTitle = isPositionLinked ? node.position : node.text;
  const subTitle = !isSpecialMode && isPositionLinked ? node.user : null;
  const avatarUrl = !isSpecialMode && isPositionLinked ? getAuthenticatedAvatarUrl(node.userAvatar) : null;
  const shouldShowAvatar = isPositionLinked && !isSpecialMode;
  const hasLoadedChildren = node.children && node.children.length > 0;
  const hasChildren = hasLoadedChildren || !!node.hasChildren;
  const isCollapsed = hasLoadedChildren ? collapsedIds.has(node.id) : true;
  const isLoadingChildren = loadingNodeIds?.has(node.id);

  return (
    <div className="org-node-container" style={{ position: 'relative' }}>
      <Paper
        elevation={2}
        className="org-node-card"
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: isCustomPosition ? '2px dashed' : '2px solid',
          borderColor: isCustomPosition ? 'warning.main' : isPositionLinked ? 'primary.light' : 'grey.300',
          width: 220,
          mx: 'auto',
          bgcolor: isCustomPosition ? '#fffdf9' : '#ffffff',
          position: 'relative',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: isCustomPosition ? 'warning.dark' : 'primary.main',
            boxShadow: 4
          },
          ...(highlightNodeId === node.id && {
            borderColor: 'warning.main',
            boxShadow: '0 0 15px rgba(237, 108, 2, 0.4)',
            borderWidth: '3px'
          })
        }}
        onClick={() => onNodeClick && onNodeClick(node)}
      >
        <Tooltip title="Ocultar lateralmente">
          <IconButton 
            size="small" 
            onClick={(e) => { e.stopPropagation(); toggleVisibility(node.id) }}
            sx={{ 
              position: 'absolute', 
              top: -10, 
              right: -10, 
              bgcolor: 'white', 
              boxShadow: 1,
              zIndex: 2,
              '&:hover': { bgcolor: 'error.light', color: 'white' }
            }}
          >
            <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {shouldShowAvatar ? (
            <Avatar 
              src={avatarUrl} 
              sx={{ 
                width: 40, 
                height: 40, 
                bgcolor: isCustomPosition ? '#ff9800' : 'primary.main',
                border: '2px solid white',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
              }}
            >
              {isCustomPosition
                ? <LabelOutlinedIcon fontSize="small" />
                : <PersonIcon fontSize="small" />}
            </Avatar>
          ) : (
            <Box sx={{ 
              width: 36, 
              height: 36, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              borderRadius: 1,
              bgcolor: 'grey.100',
              color: 'grey.600',
              flexShrink: 0
            }}>
              <AccountTreeIcon fontSize="small" />
            </Box>
          )}
          <Box sx={{ 
            textAlign: shouldShowAvatar ? 'left' : 'center', 
            overflow: 'hidden', 
            flex: 1 
          }}>
            {isCustomPosition && (
              <Chip
                label="Personalizado"
                size="small"
                variant="outlined"
                color="warning"
                sx={{ height: 16, fontSize: '0.55rem', mb: 0.3 }}
              />
            )}
            <Tooltip
              title={mainTitle || 'Sin título'}
              arrow
              placement="top"
              enterDelay={200}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 'bold', 
                  display: 'block', 
                  whiteSpace: 'nowrap', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  textTransform: isPositionLinked ? 'none' : 'uppercase',
                  color: isCustomPosition ? '#d84315' : 'inherit'
                }}
              >
                {mainTitle || 'Sin título'}
              </Typography>
            </Tooltip>
            {subTitle && (
              <Tooltip
                title={subTitle}
                arrow
                placement="bottom"
                enterDelay={200}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {subTitle}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Stack>

        {hasChildren && (
          <IconButton 
            size="small" 
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!hasLoadedChildren && typeof onExpandNode === 'function') {
                await onExpandNode(node);
                return;
              }
              toggleNode(node.id);
            }}
            sx={{ 
              position: 'absolute', 
              bottom: -20, 
              left: '50%', 
              transform: 'translateX(-50%)',
              bgcolor: 'white',
              boxShadow: 3,
              p: 0.5,
              zIndex: 100,
              border: '2px solid',
              borderColor: 'primary.light',
              '&:hover': { 
                bgcolor: 'primary.50', 
                borderColor: 'primary.main',
                transform: 'translateX(-50%)'
              }
            }}
          >
            {isCollapsed ? (
              <AddBoxIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            ) : (
              <IndeterminateCheckBoxIcon sx={{ fontSize: 20, color: 'grey.500' }} />
            )}
          </IconButton>
        )}

        {isLoadingChildren && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
            Cargando...
          </Typography>
        )}
      </Paper>
      
      {hasChildren && !isCollapsed && (
        <ul className="org-children">
          {node.children.map(child => (
            <li key={child.id}>
              <OrgNode 
                node={child} 
                collapsedIds={collapsedIds} 
                toggleNode={toggleNode} 
                onNodeClick={onNodeClick}
                highlightNodeId={highlightNodeId}
                hiddenIds={hiddenIds}
                toggleVisibility={toggleVisibility}
                onExpandNode={onExpandNode}
                loadingNodeIds={loadingNodeIds}
                displayMode={displayMode}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

OrgNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string,
    position: PropTypes.string,
    positionId: PropTypes.number,
    jobPositionId: PropTypes.number,
    isOfficialPosition: PropTypes.bool,
    user: PropTypes.string,
    userAvatar: PropTypes.string,
    hasChildren: PropTypes.bool,
    children: PropTypes.array
  }),
  collapsedIds: PropTypes.instanceOf(Set).isRequired,
  toggleNode: PropTypes.func.isRequired,
  onNodeClick: PropTypes.func,
  highlightNodeId: PropTypes.number,
  hiddenIds: PropTypes.instanceOf(Set).isRequired,
  toggleVisibility: PropTypes.func,
  onExpandNode: PropTypes.func,
  loadingNodeIds: PropTypes.instanceOf(Set),
  displayMode: PropTypes.oneOf(['default', 'holidays', 'vacaciones', 'extras'])
};

const OrgChartTreeView = ({ data, onNodeClick, targetNodeId, onExpandNode, loadingNodeIds, initialCollapsedIds, collapsedIds: externalCollapsedIds, onToggleNode, displayMode = 'default' }) => {
  const [internalCollapsedIds, setInternalCollapsedIds] = useState(new Set());
  const collapsedIds = externalCollapsedIds || internalCollapsedIds;
  const setCollapsedIds = onToggleNode 
    ? () => {} // Managed by parent
    : setInternalCollapsedIds;

  const appliedInitialRef = useRef(false);
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [scale, setScale] = useState(0.8);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const scaleRef = useRef(0.8);
  const translateRef = useRef({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const activePointerIdRef = useRef(null);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const hasAutoCenteredRef = useRef(false);

  // Sync ref with state for fast dragging
  useEffect(() => {
    translateRef.current = translate;
  }, [translate]);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  // Seed collapsed IDs from parent when data first loads
  useEffect(() => {
    if (initialCollapsedIds && initialCollapsedIds.size > 0 && !appliedInitialRef.current) {
      setCollapsedIds(new Set(initialCollapsedIds));
      appliedInitialRef.current = true;
    }
  }, [initialCollapsedIds]);

  const toggleNode = (id) => {
    if (onToggleNode) {
      onToggleNode(id);
    } else {
      setInternalCollapsedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }
  };

  const toggleVisibility = (id) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateRenderedTransform = (nextTranslate, nextScale) => {
    translateRef.current = nextTranslate;
    scaleRef.current = nextScale;

    if (contentRef.current) {
      contentRef.current.style.transform = `translate(${nextTranslate.x}px, ${nextTranslate.y}px) scale(${nextScale})`;
    }
  };

  const getContainerAnchorPoint = () => {
    const container = containerRef.current;
    if (!container) {
      return { x: 0, y: 0 };
    }

    const rect = container.getBoundingClientRect();
    return {
      x: rect.width / 2,
      y: rect.height / 2,
    };
  };

  const applyZoom = (nextScale, anchorPoint) => {
    const safeAnchor = anchorPoint || getContainerAnchorPoint();
    const currentScale = scaleRef.current;
    const currentTranslate = translateRef.current;

    if (nextScale === currentScale) {
      return;
    }

    const worldX = (safeAnchor.x - currentTranslate.x) / currentScale;
    const worldY = (safeAnchor.y - currentTranslate.y) / currentScale;

    const nextTranslate = {
      x: safeAnchor.x - (worldX * nextScale),
      y: safeAnchor.y - (worldY * nextScale),
    };

    updateRenderedTransform(nextTranslate, nextScale);
    setTranslate(nextTranslate);
    setScale(nextScale);
  };

  const finishDrag = () => {
    activePointerIdRef.current = null;
    setIsDragging(false);
    setTranslate({ ...translateRef.current });
  };

  useEffect(() => {
    const handleWindowWheel = (e) => {
      const container = containerRef.current;
      if (!container || !e.ctrlKey) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const isInsideContainer = (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      );

      if (!isInsideContainer) {
        return;
      }

      e.preventDefault();

      if (activePointerIdRef.current !== null || isDragging) {
        finishDrag();
      }

      const zoomFactor = Math.exp(-e.deltaY * WHEEL_ZOOM_SENSITIVITY);
      const nextScale = Math.min(
        Math.max(scaleRef.current * zoomFactor, MIN_SCALE),
        MAX_SCALE
      );
      const anchorPoint = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      applyZoom(nextScale, anchorPoint);
    };

    window.addEventListener('wheel', handleWindowWheel, { passive: false, capture: true });
    return () => {
      window.removeEventListener('wheel', handleWindowWheel, { capture: true });
    };
  }, [isDragging]);

  const handlePointerDown = (e) => {
    if (e.button !== 0) {
      return;
    }

    if (e.target.closest('.org-node-card')) {
      return;
    }

    if (e.target.closest('button')) {
      return;
    }

    activePointerIdRef.current = e.pointerId;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setIsDragging(true);

    if (e.currentTarget && e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e) => {
    if (!isDragging || activePointerIdRef.current !== e.pointerId) {
      return;
    }

    const dx = e.clientX - lastPosRef.current.x;
    const dy = e.clientY - lastPosRef.current.y;

    const newX = translateRef.current.x + dx;
    const newY = translateRef.current.y + dy;

    updateRenderedTransform({ x: newX, y: newY }, scaleRef.current);

    lastPosRef.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerUp = (e) => {
    if (activePointerIdRef.current !== e.pointerId) {
      return;
    }

    finishDrag();
  };

  const handlePointerCancel = (e) => {
    if (activePointerIdRef.current !== e.pointerId) {
      return;
    }

    finishDrag();
  };

  const centerTree = () => {
    const nextTranslate = { x: 0, y: 50 };
    updateRenderedTransform(nextTranslate, 0.8);
    setScale(0.8);
    setTranslate(nextTranslate);
  };

  useEffect(() => {
    if (!data || data.length === 0) {
      hasAutoCenteredRef.current = false;
      return;
    }

    if (hasAutoCenteredRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      centerTree();
      hasAutoCenteredRef.current = true;
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [data]);

  const zoomIn = () => {
    const nextScale = Math.min(scaleRef.current + SCALE_STEP, MAX_SCALE);
    applyZoom(nextScale);
  };

  const zoomOut = () => {
    const nextScale = Math.max(scaleRef.current - SCALE_STEP, MIN_SCALE);
    applyZoom(nextScale);
  };

  useEffect(() => {
    if (targetNodeId && data) {
      const targetExistsInLoadedData = data.some((n) => n.id === targetNodeId);
      if (!targetExistsInLoadedData) {
        return;
      }

      // Find path to root
      const pathIds = new Set();
      let currentId = targetNodeId;
      while (currentId) {
        pathIds.add(currentId);
        const node = data.find(n => n.id === currentId);
        currentId = node ? node.parent : null;
      }

      // Strict Isolation: hide everything that is NOT in the path but shares a parent with the path
      const nextHidden = new Set();
      const nextCollapsed = new Set();

      data.forEach(node => {
        // If it's NOT in the path
        if (!pathIds.has(node.id)) {
          // If it's a sibling of any node in the path (i.e. parent is in the path)
          if (!node.parent || pathIds.has(node.parent)) {
            nextHidden.add(node.id);
          } else {
            // It's in another branch entirely, also hide it if we want strict isolation
            nextHidden.add(node.id);
          }
        } else {
          // It IS in the path, make sure it's not hidden
          nextHidden.delete(node.id);
          // And ancestors are not collapsed
        }
      });

      setHiddenIds(nextHidden);
      
      if (onToggleNode) {
        // We can't easily clear all with onToggleNode without a specialized clear function
        // but for now, we'll keep the path expansion logic in PublicOrgChartView
      } else {
        setInternalCollapsedIds(nextCollapsed);
      }
    } else {
      // If we clear search, maybe we want to restore?
      // For now, keep it as is.
    }
  }, [targetNodeId, data]);

  if (!data || data.length === 0) return null;
  const nestedData = buildNestedTree(data);


  return (
    <Box 
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onLostPointerCapture={finishDrag}
      sx={{ 
        width: '100%', 
        height: '70vh',
        position: 'relative',
        bgcolor: '#f8f9fa',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}
    >
      {/* Controles flotantes */}
      <Box
        sx={{ 
          position: 'absolute', 
          top: 16, 
        right: 16, 
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      >
        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          <ButtonGroup orientation="vertical" size="small">
            <Tooltip title="Zoom In" placement="left">
              <IconButton onClick={zoomIn}><ZoomInIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Zoom Out" placement="left">
              <IconButton onClick={zoomOut}><ZoomOutIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Centrar organigrama" placement="left">
              <IconButton onClick={centerTree}><CenterFocusStrongIcon /></IconButton>
            </Tooltip>
          </ButtonGroup>
        </Paper>
      </Box>

      <Box 
        ref={contentRef}
        sx={{ 
          display: 'inline-block',
          width: 'max-content',
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: 'center top',
          transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)',
          pt: 10,
          willChange: 'transform',
          '& .org-tree': {
            display: 'flex',
            justifyContent: 'center'
          },
          '& ul': {
            paddingTop: '25px',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            paddingLeft: 0,
            listStyle: 'none'
          },
          '& li': {
            float: 'left',
            textAlign: 'center',
            listStyleType: 'none',
            position: 'relative',
            padding: '25px 5px 0 5px',
          },
          /* Conectores horizontales */
          '& li::before, & li::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: '50%',
            borderTop: '2px solid #0492D4',
            width: '50%',
            height: '25px',
            zIndex: 1
          },
          '& li::after': {
            right: 'auto',
            left: '50%',
            borderLeft: '2px solid #0492D4'
          },
          '& li:only-child::after, & li:only-child::before': {
            display: 'none'
          },
          '& li:only-child': {
            paddingTop: 0
          },
          '& li:first-of-type::before, & li:last-of-type::after': {
            border: 0,
          },
          '& li:last-of-type::before': {
            borderRight: '2px solid #0492D4',
            borderRadius: '0 8px 0 0'
          },
          '& li:first-of-type::after': {
            borderRadius: '8px 0 0 0'
          },
          /* Conector vertical desde el padre */
          '& ul ul::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            borderLeft: '2px solid #0492D4',
            width: 0,
            height: '25px'
          }
        }}
      >
        <div className="org-tree">
          <ul>
            {nestedData.map(rootNode => (
              <li key={rootNode.id}>
                <OrgNode 
                  node={rootNode} 
                  collapsedIds={collapsedIds} 
                  toggleNode={toggleNode} 
                  onNodeClick={onNodeClick}
                  highlightNodeId={targetNodeId}
                  hiddenIds={hiddenIds}
                  toggleVisibility={toggleVisibility}
                  onExpandNode={onExpandNode}
                  loadingNodeIds={loadingNodeIds}
                  displayMode={displayMode}
                />
              </li>
            ))}
          </ul>
        </div>
      </Box>

      {/* Leyenda de ayuda */}
      <Box sx={{ position: 'absolute', bottom: 16, right: 16, bgcolor: 'rgba(255,255,255,0.7)', px: 1, borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Arrastra para mover • Ctrl + Scroll para Zoom
        </Typography>
      </Box>
    </Box>
  );
};

OrgChartTreeView.propTypes = {
  data: PropTypes.array,
  onNodeClick: PropTypes.func,
  targetNodeId: PropTypes.number,
  onExpandNode: PropTypes.func,
  loadingNodeIds: PropTypes.instanceOf(Set),
  initialCollapsedIds: PropTypes.instanceOf(Set),
  collapsedIds: PropTypes.instanceOf(Set),
  onToggleNode: PropTypes.func,
  displayMode: PropTypes.oneOf(['default', 'holidays', 'vacaciones', 'extras'])
};

export default OrgChartTreeView;
